// CalibrationLogsTab.js
// Smart tab for the standalone Calibration Log Book: list (active/completed), create, and open a book.

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../../../store/appStore";
import { useUserState } from "../../../../context/user";
import ConfirmationModal from "../../../common/ConfirmationModal";
import {
  parseCalibrationLogBookRoute,
  buildCalibrationLogBookPath,
  canonicalizeCalibrationLogBookLocation,
} from "../../../../services/CalibrationLogBookRouteUtils";
import {
  ActiveCalibrationLogBooksTable,
  CompletedCalibrationLogBooksTable,
} from "./CalibrationLogsTable";
import { CalibrationLogBookForm } from "./CalibrationLogBookForm";
import { CalibrationLogsFilterMenu } from "./CalibrationLogsFilterMenu";
import { CALIBRATION_LOG_BOOK_VIEWS } from "../../../../store/ui/landingPage/calibrationLogsTab/uiLandingPageCalibrationLogsTabState";
import {
  createCalibrationLogBook,
  deleteCalibrationLogBook,
} from "../../../../api/CalibrationLogsApi";
import {
  startTask,
  getTaskStatus,
  downloadTaskResult,
} from "../../../../api/TasksApi";
import {
  disconnectCalibrationLogBookSocket,
  clearCalibrationLogBookHandlers,
} from "../../../../services/calibrationLogBookSocketService";
import { useFilters } from "../../../../hooks/useFilters";
import { useFilterMenu } from "../../../../hooks/useFilterMenu";
import { FilterButton } from "../buttons/DelActions";
import styles from "../sampling_events/SamplingEventsTab.module.css";

export const CalibrationLogsTab = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { isActive } = useAppStore((s) => s.loading, shallow);
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);
  const { showNotification } = useAppStore(
    (s) => s.notificationActions,
    shallow
  );

  // Tab UI
  const currentView = useAppStore(
    (s) => s.calibrationLogsTabUI.currentView,
    shallow
  );
  const activeSubTab = useAppStore(
    (s) => s.calibrationLogsTabUI.activeTab,
    shallow
  );
  const uiActions = useAppStore((s) => s.calibrationLogsTabUIActions, shallow);

  // List slices
  const activeBooks = useAppStore(
    (s) => s.calibrationLogsList.active.items,
    shallow
  );
  const completedBooks = useAppStore(
    (s) => s.calibrationLogsList.completed.items,
    shallow
  );
  const activePagination = useAppStore(
    (s) => s.calibrationLogsList.active.pagination,
    shallow
  );
  const completedPagination = useAppStore(
    (s) => s.calibrationLogsList.completed.pagination,
    shallow
  );
  const selectedActiveId = useAppStore(
    (s) => s.calibrationLogsList.active.selectedId,
    shallow
  );
  const selectedCompletedId = useAppStore(
    (s) => s.calibrationLogsList.completed.selectedId,
    shallow
  );
  const listActions = useAppStore((s) => s.calibrationLogsListActions, shallow);

  // Reference data for the filter menu
  const allProjectInfo = useAppStore((s) => s.projects.list, shallow);
  const allInactiveProjects = useAppStore(
    (s) => s.projects.inactiveList,
    shallow
  );
  const employees = useAppStore((s) => s.employees.list, shallow);
  const terminatedEmployees = useAppStore(
    (s) => s.employees.terminatedList,
    shallow
  );
  const allSearchEmployees = [
    ...(employees || []),
    ...(terminatedEmployees || []),
  ];

  // Current user + delete authorization (access level >= 2 OR the book's creator)
  const user = useUserState().currentUser;
  const accessLevel = Number(user?.access_level) || 0;
  const canDeleteBook = (book) =>
    !!book && (accessLevel >= 2 || book.created_by === user?.employee_id);

  // Delete confirmation modal state
  const [bookToDelete, setBookToDelete] = useState(null);

  const handleConfirmDelete = async () => {
    const bookId = bookToDelete;
    setBookToDelete(null);
    if (!bookId) return;
    showLoading("Deleting calibration log book...");
    try {
      await deleteCalibrationLogBook(bookId);
      showNotification({
        type: "success",
        title: "Calibration log book deleted",
      });
      if (isCompletedTab) {
        listActions.setSelectedCompletedCalibrationLogBook(null);
        await listActions.fetchCompletedCalibrationLogBooksPage(
          completedFilterCtl.filters
        );
      } else {
        listActions.setSelectedActiveCalibrationLogBook(null);
        await listActions.fetchActiveCalibrationLogBooksPage(
          activeFilterCtl.filters
        );
      }
    } catch (e) {
      const status = e?.response?.status;
      // The backend returns { error: [...] | "..." }; coerce to a string for React.
      const rawError = e?.response?.data?.error;
      const message = Array.isArray(rawError)
        ? rawError.join("; ")
        : typeof rawError === "string"
        ? rawError
        : e?.message || "Unknown error";
      showNotification({
        type: "error",
        title:
          status === 403
            ? "Not authorized to delete this book"
            : "Failed to delete calibration log book",
        message,
      });
    } finally {
      hideLoading();
    }
  };

  // Filter state (local, per sub-tab) - modeled on the shared filters used by the DEL tabs
  const activeFilterCtl = useFilters({});
  const completedFilterCtl = useFilters({});
  const isCompletedTab = activeSubTab === "completed";
  const filterCtl = isCompletedTab ? completedFilterCtl : activeFilterCtl;

  // Filter menu UI (toggle, outside-click close, phase list for the project filter)
  const {
    showFilterMenu,
    toggleFilterMenu: handleToggleFilterMenu,
    filterPhaseList,
    setFilterPhaseList: filterPhaseActionFunction,
    filterMenuRef,
  } = useFilterMenu();

  const handleApplyFilters = async () => {
    handleToggleFilterMenu();
    if (isCompletedTab) {
      listActions.setCompletedListPage(1);
      await listActions.fetchCompletedCalibrationLogBooksPage(
        completedFilterCtl.filters
      );
    } else {
      listActions.setActiveListPage(1);
      await listActions.fetchActiveCalibrationLogBooksPage(
        activeFilterCtl.filters
      );
    }
  };

  const handleClearFilters = async () => {
    const cleared = filterCtl.clearFilters();
    filterPhaseActionFunction([]);
    if (isCompletedTab) {
      listActions.setCompletedListPage(1);
      await listActions.fetchCompletedCalibrationLogBooksPage(cleared);
    } else {
      listActions.setActiveListPage(1);
      await listActions.fetchActiveCalibrationLogBooksPage(cleared);
    }
  };

  // Initial load + cleanup
  useEffect(() => {
    const init = async () => {
      showLoading();
      try {
        await listActions.fetchActiveCalibrationLogBooksPage();
        await listActions.fetchCompletedCalibrationLogBooksPage();
      } catch (e) {
        console.error("Error initializing calibration log books:", e);
      } finally {
        hideLoading();
      }
    };
    init();
    return () => {
      try {
        clearCalibrationLogBookHandlers();
      } catch {}
      try {
        disconnectCalibrationLogBookSocket();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link processing
  const processedRef = useRef(null);
  useEffect(() => {
    const canonical = canonicalizeCalibrationLogBookLocation(location);
    if (canonical) {
      navigate(canonical, { replace: true });
      return;
    }

    const { mode, bookId } = parseCalibrationLogBookRoute(
      location.pathname,
      location.search
    );
    const key = `clb:${mode || "table"}:${bookId || ""}`;
    if (processedRef.current === key) return;
    processedRef.current = key;

    (async () => {
      try {
        if (mode === "book" && bookId) {
          showLoading();
          const res = await useAppStore
            .getState()
            .activeCalibrationLogBookActions.fetchActiveCalibrationLogBook(
              bookId
            );
          if (res?.ok) uiActions.showBookFormView();
          else
            showNotification({
              type: "error",
              title: "Failed to load calibration log book",
              message:
                typeof res?.error === "string" ? res.error : "Unknown error",
            });
          hideLoading();
          return;
        }
        uiActions.showTableView();
      } catch (e) {
        hideLoading();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const handleNewBook = async () => {
    showLoading("Creating calibration log book...");
    try {
      const res = await createCalibrationLogBook({});
      const bookId = res?.data?.book_id;
      if (!bookId) throw new Error("Failed to create calibration log book");
      navigate(buildCalibrationLogBookPath({ mode: "book", bookId }));
      const fetchRes = await useAppStore
        .getState()
        .activeCalibrationLogBookActions.fetchActiveCalibrationLogBook(bookId);
      if (fetchRes?.ok) uiActions.showBookFormView();
    } catch (e) {
      showNotification({
        type: "error",
        title: "Error creating calibration log book",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleOpenBook = async (bookId) => {
    if (!bookId) {
      showNotification({ type: "error", title: "No calibration log book selected" });
      return;
    }
    navigate(buildCalibrationLogBookPath({ mode: "book", bookId }));
    showLoading();
    try {
      const res = await useAppStore
        .getState()
        .activeCalibrationLogBookActions.fetchActiveCalibrationLogBook(bookId);
      if (!res?.ok) throw new Error("Failed to load calibration log book");
      uiActions.showBookFormView();
    } catch (e) {
      showNotification({
        type: "error",
        title: "Error opening calibration log book",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleBackToTable = async () => {
    uiActions.showTableView();
    navigate(buildCalibrationLogBookPath({ mode: "table" }));
    useAppStore.getState().activeCalibrationLogBookActions.clearActiveBook();
    await listActions.fetchActiveCalibrationLogBooksPage();
    await listActions.fetchCompletedCalibrationLogBooksPage();
  };

  // On-demand PDF generation for a selected book (mirrors DEL submitted/approvals + sampling events)
  const handlePdfDownload = async (bookId) => {
    if (!bookId) {
      showNotification({ type: "error", title: "No calibration log book selected" });
      return;
    }
    showLoading("Preparing PDF...");
    try {
      const { data: start } = await startTask("calibration_log_book.create_pdf", {
        book_id: bookId,
      });
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start PDF task");

      let attempts = 0;
      let ready = false;
      let failed = false;
      let errorMessage = null;
      let lastStatus = null;
      while (!ready && attempts < 60) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          failed = true;
          errorMessage = status?.error || "Task failed";
          break;
        }
        attempts += 1;
      }
      if (!ready) throw new Error("Timed out waiting for PDF");
      if (failed) throw new Error(errorMessage || "Task failed");

      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || "application/pdf",
        });
        const cd = res.headers["content-disposition"] || "";
        const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
        const filename = match
          ? decodeURIComponent(match[1])
          : "calibration_log_book.pdf";
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      showNotification({
        type: "error",
        title: "PDF failed",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleActivePageChange = async (page) => {
    listActions.setActiveListPage(page);
    await listActions.fetchActiveCalibrationLogBooksPage(activeFilterCtl.filters);
  };
  const handleCompletedPageChange = async (page) => {
    listActions.setCompletedListPage(page);
    await listActions.fetchCompletedCalibrationLogBooksPage(
      completedFilterCtl.filters
    );
  };

  const renderContent = () => {
    if (currentView === CALIBRATION_LOG_BOOK_VIEWS.BOOK_FORM) {
      return <CalibrationLogBookForm onBack={handleBackToTable} />;
    }

    // TABLE view
    return (
      <div className={styles.samplingEventsContent}>
        <div className={styles.tabButtons}>
          <button
            className={`${styles.tabButton} ${
              activeSubTab === "active" ? styles.active : ""
            }`}
            onClick={() => uiActions.setActiveTab("active")}
          >
            Active
          </button>
          <button
            className={`${styles.tabButton} ${
              activeSubTab === "completed" ? styles.active : ""
            }`}
            onClick={() => uiActions.setActiveTab("completed")}
          >
            Completed
          </button>
        </div>

        {activeSubTab === "active" ? (
          <>
            <div className={styles.tableActions} style={{ flexWrap: "wrap" }}>
              <button className={styles.reportButton} onClick={handleNewBook}>
                New Calibration Log Book
              </button>
              <button
                className={styles.reportButton}
                onClick={() => handleOpenBook(selectedActiveId)}
                disabled={!selectedActiveId}
                style={{ marginLeft: 8 }}
              >
                Open
              </button>
              <button
                className={styles.reportButton}
                onClick={() => handlePdfDownload(selectedActiveId)}
                disabled={!selectedActiveId}
                style={{ marginLeft: 8 }}
              >
                PDF
              </button>
            </div>
            <div className={styles.headerWithRefresh}>
              <h2>Active Calibration Log Books</h2>
              <div style={{ position: "relative" }} ref={filterMenuRef}>
                <FilterButton filterFunction={handleToggleFilterMenu} />
                {showFilterMenu && (
                  <CalibrationLogsFilterMenu
                    filters={activeFilterCtl.filters}
                    updateFilterAction={activeFilterCtl.updateFilter}
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={handleClearFilters}
                    allProjectInfo={allProjectInfo}
                    allInactiveProjects={allInactiveProjects}
                    allEmployees={allSearchEmployees}
                    filterPhaseList={filterPhaseList}
                    filterPhaseActionFunction={filterPhaseActionFunction}
                  />
                )}
              </div>
            </div>
            <div className={styles.tableWrapper}>
              <ActiveCalibrationLogBooksTable
                books={activeBooks}
                onRowSelect={(book) =>
                  listActions.setSelectedActiveCalibrationLogBook(book)
                }
                onDoubleClick={handleOpenBook}
                onDelete={(bookId) => setBookToDelete(bookId)}
                canDelete={canDeleteBook}
                selectedId={selectedActiveId}
                loading={false}
                error={null}
                pagination={activePagination}
                onPageChange={handleActivePageChange}
              />
            </div>
            <div className={styles.paginationControls}>
              <div className={styles.paginationLeftControls}>
                <button
                  onClick={async () => {
                    const prev = (activePagination?.currentPage || 1) - 1;
                    if (prev < 1) return;
                    await handleActivePageChange(prev);
                  }}
                  disabled={(activePagination?.currentPage || 1) === 1 || isActive}
                >
                  Previous
                </button>
                <button
                  onClick={async () => {
                    const next = (activePagination?.currentPage || 1) + 1;
                    const total = activePagination?.totalBooks || 0;
                    const size = activePagination?.booksPerPage || 10;
                    const maxPage = Math.ceil(total / size) || 1;
                    if (next > maxPage) return;
                    await handleActivePageChange(next);
                  }}
                  disabled={
                    (activePagination?.currentPage || 1) *
                      (activePagination?.booksPerPage || 10) >=
                      (activePagination?.totalBooks || 0) || isActive
                  }
                >
                  Next
                </button>
              </div>
              <span>
                {(() => {
                  const page = activePagination?.currentPage || 1;
                  const size = activePagination?.booksPerPage || 10;
                  const total = activePagination?.totalBooks || 0;
                  const start = Math.min((page - 1) * size + 1, total);
                  const end = Math.min(page * size, total);
                  return `Showing ${start} - ${end} of ${total} active books`;
                })()}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className={styles.tableActions} style={{ flexWrap: "wrap" }}>
              <button className={styles.reportButton} onClick={handleNewBook}>
                New Calibration Log Book
              </button>
              <button
                className={styles.reportButton}
                onClick={() => handleOpenBook(selectedCompletedId)}
                disabled={!selectedCompletedId}
                style={{ marginLeft: 8 }}
              >
                Open
              </button>
              <button
                className={styles.reportButton}
                onClick={() => handlePdfDownload(selectedCompletedId)}
                disabled={!selectedCompletedId}
                style={{ marginLeft: 8 }}
              >
                PDF
              </button>
            </div>
            <div className={styles.headerWithRefresh}>
              <h2>Completed Calibration Log Books</h2>
              <div style={{ position: "relative" }} ref={filterMenuRef}>
                <FilterButton filterFunction={handleToggleFilterMenu} />
                {showFilterMenu && (
                  <CalibrationLogsFilterMenu
                    filters={completedFilterCtl.filters}
                    updateFilterAction={completedFilterCtl.updateFilter}
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={handleClearFilters}
                    allProjectInfo={allProjectInfo}
                    allInactiveProjects={allInactiveProjects}
                    allEmployees={allSearchEmployees}
                    filterPhaseList={filterPhaseList}
                    filterPhaseActionFunction={filterPhaseActionFunction}
                  />
                )}
              </div>
            </div>
            <div className={styles.tableWrapper}>
              <CompletedCalibrationLogBooksTable
                books={completedBooks}
                onRowSelect={(book) =>
                  listActions.setSelectedCompletedCalibrationLogBook(book)
                }
                onDoubleClick={handleOpenBook}
                selectedId={selectedCompletedId}
                loading={false}
                error={null}
                pagination={completedPagination}
                onPageChange={handleCompletedPageChange}
              />
            </div>
            <div className={styles.paginationControls}>
              <div className={styles.paginationLeftControls}>
                <button
                  onClick={async () => {
                    const prev = (completedPagination?.currentPage || 1) - 1;
                    if (prev < 1) return;
                    await handleCompletedPageChange(prev);
                  }}
                  disabled={
                    (completedPagination?.currentPage || 1) === 1 || isActive
                  }
                >
                  Previous
                </button>
                <button
                  onClick={async () => {
                    const next = (completedPagination?.currentPage || 1) + 1;
                    const total = completedPagination?.totalBooks || 0;
                    const size = completedPagination?.booksPerPage || 10;
                    const maxPage = Math.ceil(total / size) || 1;
                    if (next > maxPage) return;
                    await handleCompletedPageChange(next);
                  }}
                  disabled={
                    (completedPagination?.currentPage || 1) *
                      (completedPagination?.booksPerPage || 10) >=
                      (completedPagination?.totalBooks || 0) || isActive
                  }
                >
                  Next
                </button>
              </div>
              <span>
                {(() => {
                  const page = completedPagination?.currentPage || 1;
                  const size = completedPagination?.booksPerPage || 10;
                  const total = completedPagination?.totalBooks || 0;
                  const start = Math.min((page - 1) * size + 1, total);
                  const end = Math.min(page * size, total);
                  return `Showing ${start} - ${end} of ${total} completed books`;
                })()}
              </span>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.samplingEventsTabContainer}>
      {renderContent()}
      <ConfirmationModal
        isOpen={!!bookToDelete}
        title="Delete Calibration Log Book"
        message="Are you sure you want to delete this calibration log book? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="deleteButton"
        onCancel={() => setBookToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};
